import React from "react";

/* ── Stat Card ── */
export function StatCard({ icon, value, label, sub, color = "blue" }) {
  const colors = {
    blue:  "from-blue-500 to-blue-600",
    green: "from-emerald-500 to-emerald-600",
    amber: "from-amber-500 to-amber-600",
    red:   "from-red-500 to-red-600",
    purple:"from-purple-500 to-purple-600",
    gray:  "from-gray-400 to-gray-500",
  };
  const bgColors = {
    blue:  "bg-blue-50",
    green: "bg-emerald-50",
    amber: "bg-amber-50",
    red:   "bg-red-50",
    purple:"bg-purple-50",
    gray:  "bg-gray-50",
  };

  return (
    <div className="stat-card">
      <div className="flex items-center gap-4">
        <div className={`w-11 h-11 rounded-lg flex items-center justify-center text-lg ${bgColors[color] || bgColors.blue}`}>
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-2xl font-bold text-gray-900 tabular-nums">{value}</div>
          <div className="text-sm text-gray-500">{label}</div>
          {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
        </div>
      </div>
    </div>
  );
}

/* ── Change Card ── */
export function ChangeCard({ change }) {
  const cls = change.classification || {};
  const urgency = cls.urgency || "monitor";
  const relevance = cls.relevance_to_oak || "low";
  const changeType = cls.change_type || "other";

  const urgencyClass =
    urgency === "immediate" ? "urgency-immediate" :
    urgency === "short_term" ? "urgency-short_term" : "urgency-monitor";

  return (
    <div className={`card ${urgencyClass}`}>
      <div className="card-body">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">{change.competitor}</h3>
            <p className="text-xs text-gray-400 truncate mt-0.5">{change.url}</p>
          </div>
          <div className="flex gap-1.5 shrink-0 flex-wrap justify-end">
            <span className="badge badge-gray capitalize">{changeType.replace(/_/g, " ")}</span>
            <span className={
              urgency === "immediate" ? "badge badge-red" :
              urgency === "short_term" ? "badge badge-yellow" :
              "badge badge-green"
            }>
              {urgency === "immediate" ? "🔴 Urgent" : urgency === "short_term" ? "🟡 Watch" : "🟢 Monitor"}
            </span>
          </div>
        </div>

        {/* Summary */}
        <p className="text-sm text-gray-600 mb-3 leading-relaxed">{change.diff_summary}</p>

        {/* Action */}
        {cls.recommended_action && (
          <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5 mb-3">
            <p className="text-sm text-blue-800 leading-relaxed">
              <span className="font-medium">→ </span>{cls.recommended_action}
            </p>
          </div>
        )}

        {/* Toggle new content */}
        {change.added && change.added.length > 0 && (
          <details className="text-sm group">
            <summary className="cursor-pointer text-emerald-600 font-medium hover:text-emerald-700 text-xs">
              ➕ View new content ({change.added.length} items)
            </summary>
            <ul className="mt-2 space-y-1 text-gray-600 max-h-36 overflow-y-auto text-xs">
              {change.added.slice(0, 10).map((item, i) => (
                <li key={i} className="pl-3 border-l-2 border-emerald-300 py-0.5">{item}</li>
              ))}
            </ul>
          </details>
        )}

        {/* Timestamp */}
        <div className="mt-3 text-[11px] text-gray-400">
          Detected {change.detected_at ? new Date(change.detected_at).toLocaleString() : "—"}
        </div>
      </div>
    </div>
  );
}

/* ── Competitor Badge ── */
const CAT_COLORS = {
  "Direct — Global Leader":    "badge badge-blue",
  "Direct — HK/Regional":      "badge badge-purple",
  "Indirect — General AI":     "badge badge-green",
  "Indirect — Native Platform":"badge badge-yellow",
  "Indirect — Productivity":   "badge badge-gray",
  "Indirect — Research":       "badge",
};
export function CompetitorBadge({ name, category }) {
  return (
    <div className="card p-3 flex items-center gap-3 transition-shadow hover:shadow-md">
      <span className="text-lg shrink-0">🏢</span>
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
        <span className={`text-[11px] ${CAT_COLORS[category] || "badge badge-gray"}`}>
          {category}
        </span>
      </div>
    </div>
  );
}
